/* ============================================================== */
/*                                                                */
/*   sequenceStateV4  ·  helper state กลางของ sequence ทุกตัวใน HIS V4 */
/*                                                                */
/*   team practice (common/sequence/PatientOrdersV4.ts):             */
/*     loading  -> state.buttonLoadCheck["<card>_<ACTION>"]         */
/*     error    -> state.errorMessage["<card>"]                     */
/*     slice    -> state["<Sequence>Sequence"] เก็บเฉพาะข้อมูล       */
/*                                                                */
/*   ไม่ได้เรียก CommonInterface.SetErrorMessage ตรง ๆ เพราะ helper นั้น */
/*   ส่ง error เข้า TextDecoder.decode() (HISV3/common/CommonInterface.ts:929) */
/*   ซึ่งรับเฉพาะ ArrayBuffer/TypedArray -> ถ้า error ถูก normalize เป็น   */
/*   string แล้วจะ TypeError key ที่ได้เหมือนกันทุกตัว                    */
/*                                                                */
/*   setState ครั้งเดียวต่อ updateหนึ่ง (กัน spread จาก state ที่ยัง batch คิ) */
/*                                                                */
/* ============================================================== */

import { BLC_STATUS, type BLC_STATUS_TYPE } from "react-lib/apps/HISV3/common/CommonInterface";

import { apiErrorTextOf, errorTextOf } from "../errorTextV4";

export type { BLC_STATUS_TYPE };
export { apiErrorTextOf, errorTextOf };

export type SeqMeta = {
  /** ชื่อ sequence เช่น "ExamV4" */
  sequence: string;
  /** card ผู้ส่ง action = prefix ของ buttonLoadCheck และ key ของ errorMessage */
  card?: string;
  action?: string;
};

/** <card>_<action> — format เดียวกับ CommonInterface.SetErrorMessage */
export const seqBtnKey = (meta: SeqMeta): string =>
  `${meta.card ?? meta.sequence}_${meta.action ?? ""}`;

/** errorMessage ใช้ชื่อ card ถ้าไม่มี card ใช้ชื่อ sequence */
export const seqErrorKey = (meta: SeqMeta): string => meta.card ?? meta.sequence;

type StateStore = {
  getState: () => Record<string, any>;
  setState: (state: any, callback?: () => void) => any;
};

export type SlicePatch<S> = Partial<S> & {
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
};

/**
 * เขียน slice + buttonLoadCheck + errorMessage ด้วยการ setState ครั้งเดียว
 *
 * loading/saving/error ไม่เก็บใน slice: ค่าเหล่านี้ถูกถอดออกจาก patch
 * แล้วไปอยู่ใน state กลางของ app ตาม practice ของทีม
 */
export const writeSequenceState = <S extends object>(
  controller: StateStore,
  meta: SeqMeta,
  sliceKey: string,
  current: S | null | undefined,
  patch: SlicePatch<S>,
  empty: () => S,
): any => {
  const state = controller.getState();
  const { loading, saving, error, ...data } = patch;

  const buttonLoadCheck = { ...state.buttonLoadCheck };
  const errorMessage = { ...state.errorMessage };
  const key = seqBtnKey(meta);

  if (loading === true || saving === true) buttonLoadCheck[key] = BLC_STATUS.LOADING;

  if (error !== undefined) {
    if (error === null) {
      errorMessage[seqErrorKey(meta)] = null;
      if (loading !== true && saving !== true) buttonLoadCheck[key] = BLC_STATUS.SUCCESS;
    } else {
      errorMessage[seqErrorKey(meta)] = error;
      buttonLoadCheck[key] = BLC_STATUS.ERROR;
    }
  } else if (loading === false || saving === false) {
    buttonLoadCheck[key] = BLC_STATUS.SUCCESS;
  }

  return controller.setState({
    [sliceKey]: { ...empty(), ...current, ...data },
    buttonLoadCheck,
    errorMessage,
  });
};

/** ตั้ง buttonLoadCheck หลาย key พร้อมกัน (Start โหลดหลายหน้าพร้อมกัน) */
export const markButtonLoadCheck = (
  controller: StateStore,
  metas: SeqMeta[],
  status: BLC_STATUS_TYPE,
): void => {
  const state = controller.getState();
  const buttonLoadCheck = { ...state.buttonLoadCheck };
  metas.forEach((meta) => {
    buttonLoadCheck[seqBtnKey(meta)] = status;
  });
  controller.setState({ buttonLoadCheck });
};

/** อ่าน loading ของ 1 action จาก state กลาง */
export const isButtonLoading = (
  buttonLoadCheck: Record<string, any> | undefined,
  meta: SeqMeta,
): boolean => (buttonLoadCheck ? buttonLoadCheck[seqBtnKey(meta)] === BLC_STATUS.LOADING : false);
